import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Phone,
  Award,
  Building2,
  Gavel,
  ShieldCheck,
  Trophy,
  MapPin,
  CheckCircle2,
  HeartPulse,
} from "lucide-react";
import {
  FIRM,
  BADGES,
  PRACTICE_AREAS,
  TESTIMONIALS,
  IMAGES,
  RESULTS_STATS,
  CASE_RESULTS,
  SERVICE_AREAS,
} from "@/lib/site";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import PracticeCard from "@/components/PracticeCard";
import WhyHire from "@/components/WhyHire";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import Seo from "@/components/Seo";

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        title="Massachusetts Personal Injury & Criminal Defense Attorney | Mahaney Law"
        description="Attorney Joseph M. Mahaney — 40+ years of trial experience in personal injury and criminal defense. Dedicated to my clients' success. Serving all of Massachusetts. Free consultation."
        path="/"
      />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero_photo})` }}
          data-testid="hero-background"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/90 via-navy-950/60 to-navy-950/25" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-12 lg:py-14 min-h-[600px] lg:min-h-[62vh] flex items-center">
          <div className="flex flex-col lg:flex-row lg:justify-between gap-10 items-stretch w-full">
            {/* Left content panel */}
            <motion.div
              className="lg:w-[52%]"
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
                <div className="h-full flex flex-col justify-center rounded-md bg-navy-950/60 backdrop-blur-sm border border-white/10 p-7 md:p-10 lg:p-11 shadow-2xl">
                  <div className="flex flex-wrap items-center gap-2 self-start">
                    <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-3.5 py-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                      <span className="text-[0.65rem] sm:text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-gold">
                        {FIRM.tagline}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-3.5 py-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                      <span className="text-[0.65rem] sm:text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-gold">
                        {FIRM.serviceArea}
                      </span>
                    </span>
                  </div>
                  <h1 className="mt-6 font-display text-[2.1rem] sm:text-5xl lg:text-[3.75rem] leading-[1.05] tracking-tight text-white text-balance">
                    When Results{" "}
                    <span className="italic font-medium text-gold">
                      Matter
                    </span>
                  </h1>
                  <div className="divider-gold mt-6" />
                  <p className="mt-6 text-lg md:text-xl text-white font-medium">
                    Massachusetts{" "}
                    <span className="text-gold">
                      Personal Injury & Criminal Defense
                    </span>{" "}
                    Attorney
                  </p>
                  <p className="mt-3 max-w-xl text-base md:text-lg text-white/75 leading-relaxed font-light">
                    Joseph M. Mahaney — 40+ years of trial experience fighting for
                    clients throughout the Commonwealth.
                  </p>
                  <div className="mt-7 flex flex-col sm:flex-row gap-4">
                    <Link
                      to="/contact"
                      data-testid="hero-primary-cta"
                      className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold tracking-wide hover:bg-gold-light transition-all"
                    >
                      Free Consultation
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                    <a
                      href={`tel:${FIRM.phoneHref}`}
                      data-testid="hero-call-now-cta"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm bg-transparent border-2 border-white/50 text-white text-sm font-semibold tracking-wide hover:bg-white hover:text-navy-950 transition-all"
                    >
                      <Phone className="h-4 w-4" />
                      Call Now
                    </a>
                  </div>
                </div>
            </motion.div>

            {/* Right headshot card */}
            <motion.div
              className="lg:w-[340px] xl:w-[380px] flex-shrink-0"
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="relative h-full min-h-[460px] rounded-md overflow-hidden border-2 border-gold bg-navy-900 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
                  <img
                    src={IMAGES.headshot}
                    alt="Attorney Joseph M. Mahaney"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    data-testid="hero-attorney-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/15 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">
                      Trial Attorney
                    </div>
                    <div className="font-display text-2xl text-white mt-2">
                      Joseph M. Mahaney
                    </div>
                    <div className="text-xs text-white/70 mt-1">
                      Massachusetts • Federal & State Courts
                    </div>
                  </div>
                </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section data-testid="stats-bar" className="bg-white border-b border-navy-900/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {BADGES.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div
                  data-testid={`stat-${i}`}
                  className="px-0 lg:px-8 py-6 lg:py-0 lg:border-l border-navy-900/10 first:border-l-0 first:pl-0 lg:first:pl-0"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-full border-2 border-gold/40 grid place-items-center flex-shrink-0">
                      <b.icon className="h-7 w-7 text-gold" />
                    </div>
                    <div>
                      <div className="font-display text-3xl md:text-[2.1rem] text-navy-900 leading-none">
                        {b.value}
                      </div>
                      <div className="font-display text-lg text-navy-900 mt-1 leading-tight min-h-[2.75rem]">
                        {b.label}
                      </div>
                    </div>
                  </div>
                  <div className="h-px w-12 bg-gold mt-5" />
                  <p className="mt-4 text-sm text-navy-900/60 leading-relaxed">
                    {b.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARALLAX */}
      <section
        data-testid="parallax-section"
        className="relative"
      >
        <div
          className="parallax absolute inset-0"
          style={{ backgroundImage: `url(${IMAGES.parallax})` }}
        />
        <div className="absolute inset-0 bg-navy-950/75" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-28 md:py-40 text-center">
          <Reveal>
            <div className="eyebrow text-gold">Four Decades of Advocacy</div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-[3.5rem] leading-[1.1] tracking-tight text-white mt-5 text-balance">
              Experience that stands on a
              <span className="block italic text-gold">foundation of trust.</span>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-white/75 text-lg font-light leading-relaxed">
              For more than 40 years, Attorney Joseph M. Mahaney has stood beside
              Massachusetts clients in their most difficult moments — preparing
              every case with the diligence of a seasoned trial attorney.
            </p>
            <Link
              to="/contact"
              data-testid="parallax-cta"
              className="inline-flex items-center justify-center gap-2 mt-9 px-8 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
            >
              Request a Free Consultation
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FOCUS BAND — Personal Injury & Criminal Defense */}
      <section
        data-testid="focus-band-section"
        className="bg-white py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <div className="rounded-sm border border-navy-900/10 bg-navy-50 p-8 md:p-14 text-center">
              <div className="eyebrow text-gold">Areas of Focus</div>
              <h2 className="font-display text-3xl md:text-5xl lg:text-[3.25rem] leading-[1.1] tracking-tight text-navy-900 mt-4 text-balance">
                Massachusetts Personal Injury
                <span className="block italic text-gold">& Criminal Defense</span>
              </h2>
              <p className="mt-6 max-w-2xl mx-auto text-navy-900/65 text-lg font-light leading-relaxed">
                Two focused practices, one trial-tested attorney. Whether you've
                been injured by negligence or are facing criminal charges,
                Attorney Mahaney brings four decades of courtroom experience to
                your side.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/practice-areas/personal-injury"
                  data-testid="focus-pi-link"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
                >
                  <HeartPulse className="h-4 w-4" /> Personal Injury
                </Link>
                <Link
                  to="/practice-areas/criminal-defense"
                  data-testid="focus-cd-link"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm border-2 border-navy-900 text-navy-900 text-sm font-semibold hover:bg-navy-900 hover:text-white transition-all"
                >
                  <Gavel className="h-4 w-4" /> Criminal Defense
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRACTICE AREAS */}
      <section
        data-testid="practice-areas-section"
        className="py-20 md:py-28 bg-navy-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
            <SectionHeading
              eyebrow="Practice Areas"
              title={
                <>
                  Decades of trial work,
                  <br />
                  in the matters that change lives.
                </>
              }
              testId="practice"
            />
            <Reveal delay={0.1}>
              <Link
                to="/practice-areas"
                data-testid="view-all-practice-areas"
                className="inline-flex items-center gap-2 text-sm font-medium text-navy-900 hover:text-gold transition-colors"
              >
                View all practice areas
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRACTICE_AREAS.map((area, i) => (
              <Reveal key={area.slug} delay={i * 0.05}>
                <PracticeCard area={area} index={i} />
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link
                to="/contact"
                data-testid="practice-card-more"
                className="group relative flex flex-col justify-between overflow-hidden rounded-sm ink-gradient grain p-8 text-white min-h-[260px]"
              >
                <div>
                  <div className="eyebrow text-gold">Free Consultation</div>
                  <h3 className="font-display text-2xl md:text-[1.6rem] mt-6 leading-tight">
                    Not sure where your case fits? Let's talk.
                  </h3>
                </div>
                <div className="mt-8 flex items-center justify-between text-gold">
                  <span className="text-sm font-semibold">Contact Mahaney Law</span>
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ATTORNEY INTRO */}
      <section
        data-testid="attorney-intro-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="relative rounded-sm overflow-hidden aspect-[4/5] bg-navy-900 border-2 border-gold shadow-[0_30px_70px_-40px_rgba(10,25,47,0.6)]">
                  <img
                    src={IMAGES.headshot}
                    alt="Attorney Joseph M. Mahaney"
                    className="w-full h-full object-cover object-top"
                    data-testid="home-attorney-profile-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/10 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 glass-dark rounded-sm p-5 text-white">
                    <div className="eyebrow text-gold">Joseph M. Mahaney</div>
                    <div className="font-display text-xl mt-2 leading-tight">
                      Massachusetts Trial Attorney
                    </div>
                    <div className="text-xs text-white/70 mt-1">
                      Personal Injury • Criminal Defense
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="Meet Attorney Mahaney"
                title="Dedicated to my clients' success — for over 40 years."
                body="From his early years as an Assistant District Attorney to 17 years as a litigation partner, Joseph M. Mahaney has spent four decades preparing every case as if it will be tried."
                testId="attorney"
              />
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { icon: Award, label: "40+ Years", text: "Litigation across personal injury & criminal defense." },
                  { icon: Gavel, label: "Former ADA", text: "Suffolk County District Attorney's Office." },
                  { icon: Building2, label: "Former Partner", text: "17 years at McLaughlin, Richards & Mahaney, LLP." },
                  { icon: ShieldCheck, label: "State to SCOTUS", text: "Trial & appellate practice up to the U.S. Supreme Court." },
                ].map((p, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <div className="rounded-sm border border-navy-900/10 bg-white p-6 h-full hover:border-gold/50 transition-colors duration-300">
                      <p.icon className="h-5 w-5 text-gold" />
                      <div className="mt-4 font-display text-lg text-navy-900">
                        {p.label}
                      </div>
                      <p className="text-sm text-navy-900/60 mt-2 leading-relaxed">
                        {p.text}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <Link
                  to="/about"
                  data-testid="learn-about-attorney-link"
                  className="inline-flex items-center gap-2 mt-10 px-7 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
                >
                  More about Attorney Mahaney
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SERVING ALL OF MASSACHUSETTS */}
      <ServingMassachusetts />

      {/* PROVEN RESULTS (dark accent band) */}
      <section
        data-testid="proven-results-section"
        className="py-20 md:py-28 ink-gradient grain text-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <div className="eyebrow text-gold">Proven Results</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight mt-4 text-white text-balance">
              Decades of results across
              <span className="block italic text-gold">
                Massachusetts & federal courts.
              </span>
            </h2>
            <div className="divider-gold mt-8" />
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CASE_RESULTS.map((r, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  data-testid={`case-result-${i}`}
                  className="rounded-sm border border-white/10 bg-white/5 p-7 h-full hover:border-gold/40 transition-colors duration-300"
                >
                  <Trophy className="h-6 w-6 text-gold" />
                  <div className="font-display text-3xl md:text-[2.25rem] text-white leading-none mt-6">
                    {r.value}
                  </div>
                  <div className="mt-3 text-sm font-medium text-white/85">
                    {r.label}
                  </div>
                  <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">
                    {r.category}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <p className="text-xs text-white/40 italic">
                Prior results do not guarantee future outcomes.
              </p>
              <Link
                to="/results"
                data-testid="view-results-link"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all self-start md:self-auto"
              >
                Explore Results & Experience
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {RESULTS_STATS.map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="rounded-sm border border-white/10 bg-white/5 p-6">
                  <div className="font-display text-2xl md:text-3xl text-gold leading-none">
                    {s.value}
                  </div>
                  <div className="mt-3 text-xs uppercase tracking-[0.18em] text-white/55 font-semibold leading-snug">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY HIRE */}
      <section data-testid="why-hire-home" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <WhyHire />
          <Reveal delay={0.2}>
            <div className="mt-12">
              <Link
                to="/contact"
                data-testid="why-hire-cta"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
              >
                Schedule your free consultation
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PARALLAX 2 */}
      <section data-testid="parallax-section-2" className="relative">
        <div
          className="parallax absolute inset-0"
          style={{ backgroundImage: `url(${IMAGES.parallax2})` }}
        />
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 text-center">
          <Reveal>
            <div className="eyebrow text-gold">A Measured Approach</div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-[3.25rem] leading-[1.1] tracking-tight text-white mt-5 text-balance">
              Justice is built on
              <span className="block italic text-gold">preparation and balance.</span>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-white/75 text-lg font-light leading-relaxed">
              Every case is weighed with care, prepared with precision, and
              argued with the conviction of an attorney who has spent four
              decades in Massachusetts courtrooms.
            </p>
            <Link
              to="/contact"
              data-testid="parallax2-cta"
              className="inline-flex items-center justify-center gap-2 mt-9 px-8 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-light transition-all"
            >
              Schedule Your Consultation
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>


      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-preview-section"
        className="py-20 md:py-28 bg-navy-50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <SectionHeading
            align="center"
            eyebrow="Client Reviews"
            title={<>What clients say after their case.</>}
            body="Every result is unique. The trust clients place in our firm is what we work hardest to earn."
            testId="testimonials"
          />
          <TestimonialCarousel testimonials={TESTIMONIALS} />
          <Reveal delay={0.2}>
            <div className="mt-12 text-center">
              <Link
                to="/testimonials"
                data-testid="view-all-testimonials"
                className="inline-flex items-center gap-2 text-sm font-medium text-navy-900 hover:text-gold transition-colors"
              >
                Read all testimonials
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <FinalCTA />
    </div>
  );
}

export function ServingMassachusetts() {
  return (
    <section
      data-testid="serving-massachusetts-section"
      className="py-20 md:py-28 bg-navy-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 text-gold">
                <MapPin className="h-5 w-5" />
                <span className="eyebrow">Service Area</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-navy-900 mt-4 text-balance">
                Serving All of
                <span className="block italic text-gold">Massachusetts.</span>
              </h2>
              <p className="mt-6 max-w-xl text-navy-900/65 text-lg font-light leading-relaxed">
                From Boston to the South Shore, the North Shore, and Central
                Massachusetts — Attorney Mahaney represents clients in courts
                throughout the Commonwealth.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="grid grid-cols-2 gap-3">
                {SERVICE_AREAS.map((area, i) => (
                  <div
                    key={i}
                    data-testid={`service-area-${i}`}
                    className="flex items-center gap-3 rounded-sm border border-navy-900/10 bg-white px-5 py-4"
                  >
                    <CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0" />
                    <span className="text-sm text-navy-900/85">{area}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section
      data-testid="final-cta-section"
      className="relative py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-sm ink-gradient grain p-10 md:p-16 lg:p-20 text-white"
        >
          <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-gold/15 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="eyebrow text-gold">Speak with an Attorney</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight mt-4 text-balance">
                Get a free consultation with an experienced Massachusetts attorney.
              </h2>
              <p className="mt-6 max-w-2xl text-white/70 text-lg leading-relaxed font-light">
                Cases are won — or lost — in the preparation. The earlier you
                involve experienced counsel, the better your outcome.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link
                to="/contact"
                data-testid="final-cta-primary"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
              >
                Free Consultation
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${FIRM.phoneHref}`}
                data-testid="final-cta-call"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm bg-transparent border border-white/30 text-white text-sm font-medium hover:bg-white/10 transition-all"
              >
                <Phone className="h-4 w-4 text-gold" />
                Call Now
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
